import { z } from 'zod';

/**
 * Website URL の正規化
 * プロトコルが省略されている場合は https:// を追加
 */
export function normalizeWebsiteUrl(input: string): string {
  const trimmed = input.trim();

  // 空文字の場合はそのまま返す（バリデーションで弾く）
  if (!trimmed) {
    return trimmed;
  }

  // プロトコルがない場合は https:// を追加
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return trimmed;
}

/**
 * URL が有効かどうかをチェック
 * WHATWG URL constructor を使用
 */
function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Website URL バリデーションスキーマ
 *
 * 要件:
 * - 有効なURL形式 (WHATWG URL Standard 準拠)
 * - HTTPS プロトコル (セキュリティのため)
 * - 公開ウェブサイト (localhost, IP アドレスは不可)
 * - ドメイン形式の検証（最低 1 つの `.` を含む）
 * - 国際化ドメイン名 (IDN) サポート
 */
export const websiteUrlSchema = z
  .string()
  .min(1, { message: 'ウェブサイトのURLを入力してください' })
  .transform(normalizeWebsiteUrl)
  .refine((url) => isValidUrl(url), {
    message: '有効なURL形式で入力してください (例: https://example.com)',
  })
  .refine(
    (url) => {
      try {
        const parsed = new URL(url);
        // https または http のみ許可（http は後続で拒否）
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
      } catch {
        return false;
      }
    },
    { message: '有効なURL形式で入力してください (例: https://example.com)' }
  )
  .refine(
    (url) => {
      try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'セキュリティのため、HTTPSのウェブサイトのみ対応しています' }
  )
  .refine(
    (url) => {
      try {
        const parsed = new URL(url);
        const hostname = parsed.hostname.toLowerCase();

        // localhost を拒否（先にチェックして適切なエラーメッセージを返す）
        const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(
          hostname
        );

        // IPv4 アドレスを拒否
        const isIpv4 = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname);

        // IPv6 アドレスを拒否（角括弧付き）
        const isIpv6 = hostname.startsWith('[') && hostname.endsWith(']');

        return !isLocalhost && !isIpv4 && !isIpv6;
      } catch {
        return false;
      }
    },
    { message: '公開されているウェブサイトのURLを入力してください' }
  )
  .refine(
    (url) => {
      try {
        const parsed = new URL(url);
        const hostname = parsed.hostname.toLowerCase();

        // ドメイン形式の検証: 最低 1 つの `.` を含む（例: example.com）
        // 単一ワードや不完全なドメインを拒否
        return hostname.includes('.');
      } catch {
        return false;
      }
    },
    { message: '有効なURL形式で入力してください (例: https://example.com)' }
  );

/**
 * URL バリデーション結果の型
 */
export type WebsiteUrlValidationResult =
  | { success: true; data: string }
  | { success: false; error: string };

/**
 * URL をバリデーションする関数
 * コンポーネントから直接呼び出し可能
 */
export function validateWebsiteUrl(url: string): WebsiteUrlValidationResult {
  const result = websiteUrlSchema.safeParse(url);

  if (result.success) {
    return { success: true, data: result.data };
  }

  // 最初のエラーメッセージを返す
  const errorMessage =
    result.error.issues[0]?.message ?? 'URLの形式が正しくありません';
  return { success: false, error: errorMessage };
}
